import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { BulkCostAccountDefault } from './bulk-cost.model';
import { BulkCostService } from './bulk-cost.service';

@Component({
  selector: 'app-bulk-cost-account-defaults',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ToastModule, ProgressBarModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, SelectModule, TagModule, PageShellComponent,
    PageHeroComponent, PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './bulk-cost-account-defaults.component.html',
  styleUrls: ['../../finance-list.shared.css', './bulk-cost-account-defaults.component.css']
})
export class BulkCostAccountDefaultsComponent implements OnInit {
  readonly sourceTypes = [
    { value: 'MANUAL', label: 'Manual' },
    { value: 'SHIPMENT_INTERNAL_COST', label: 'Shipment Internal Cost' },
    { value: 'SHIPMENT', label: 'Shipment' },
    { value: 'DELIVERY', label: 'Delivery' },
    { value: 'SALES_ORDER', label: 'Sales Order' },
    { value: 'ORDER_RELEASE', label: 'Order Release' },
    { value: 'CLEARANCE', label: 'Clearance' },
    { value: 'WAREHOUSING', label: 'Warehousing' },
    { value: 'PROJECT', label: 'Project' }
  ];
  rows: BulkCostAccountDefault[] = [];
  accounts: ChartOfAccounts[] = [];
  isLoading = false;
  isSaving = false;

  constructor(
    private readonly service: BulkCostService,
    private readonly accountsService: ChartOfAccountsService,
    private readonly session: SessionStorageService,
    private readonly messageService: MessageService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  back(): void {
    this.router.navigate(['/accounts/enterprise/payables/bulk-costs']);
  }

  save(row: BulkCostAccountDefault): void {
    this.isSaving = true;
    this.service.saveAccountDefault({ ...row, owningCompanyId: this.companyId(), active: true }).subscribe({
      next: saved => {
        Object.assign(row, saved);
        this.isSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Account defaults', detail: `${this.sourceLabel(row.sourceType)} defaults saved.` });
      },
      error: error => {
        this.isSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Account defaults', detail: error?.error?.message || 'Could not save account defaults.' });
      }
    });
  }

  accountLabel(account: ChartOfAccounts): string {
    return `${account.accountCode || ''}${account.accountCode && account.accountName ? ' - ' : ''}${account.accountName || 'Account'}`;
  }

  sourceLabel(sourceType: string): string {
    return this.sourceTypes.find(row => row.value === sourceType)?.label || sourceType;
  }

  private load(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.isLoading = true;
    this.accountsService.getAllChartOfAccountsByCompany(companyId, false).subscribe(accounts => this.accounts = accounts || []);
    this.service.getAccountDefaults(companyId).subscribe({
      next: rows => {
        this.rows = (rows || []).map(row => ({ ...row, owningCompanyId: companyId, active: row.active !== false }));
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Account defaults', detail: error?.error?.message || 'Could not load bulk cost account defaults.' });
      }
    });
  }

  private companyId(): number {
    return this.session.getCompany()?.id || 0;
  }
}
